import { TTool } from "../../appConsts";

/**
 * The two sample reports an embedded partner widget can request. WHICH tools the widget offers
 * can't be derived from `TTool` — that is a product decision — so the codes are stated once
 * here. The `satisfies` clause ties them back: rename a tool in `appConsts` and this fails to
 * compile. (`Extract<TTool, "rerc" | "bvr">` would NOT catch that — it silently resolves to
 * `never` for a code that no longer exists.)
 *
 * Values are `TTool` keys, so a tool passes straight to `getTool`/`getToolExportUrl` untranslated.
 */
export const SAMPLE_REPORT_TOOLS = ["rerc", "bvr"] as const satisfies readonly TTool[];

export type TSampleReportTool = (typeof SAMPLE_REPORT_TOOLS)[number];

/**
 * Resolved from `zip` at submit time. Stored rather than re-derived at download so the email
 * copy and the rendered report can't disagree, and so the render doesn't repeat the
 * Google/transition lookups. `fip` is what the market-data calls key off.
 */
export interface ISampleReportLocation {
    city?: string;
    state?: string;
    /** Not redundant with `fip`: the rerc render passes `county` to `/v1/location/rerc`. */
    county?: string;
    fip?: string;
    /**
     * Google's resolved place, e.g. "North Middletown, NJ 07748, USA". REQUIRED by
     * `/v1/location/rerc`, which 400s without it — and `useRERC` maps a non-408 failure to a
     * thrown error rather than its retry, so the export page never signals ready and the render
     * times out instead of failing visibly.
     */
    address?: string;
    latitude?: string;
    longitude?: string;
}

/**
 * One sample report requested from a partner widget. Requesters are typically not Highway
 * subscribers, so this record stands alone — there is no user or client document behind it.
 *
 * One document per request, capped at two per person by the one-per-tool limit. A "lead" is
 * therefore not a document but a derived view: group these by `email`. `name`, `cell` and `nmls`
 * repeat across a person's two documents, and can legitimately differ between them if the
 * requester typed something different the second time.
 */
export interface ISampleReportRequest {
    name: string;
    /** Stored lowercased and trimmed; unique per `tool`, which is how the limit is enforced. */
    email: string;
    tool: TSampleReportTool;
    /**
     * Who the widget belongs to, copied from the WidgetEmbeds row the request's key resolved to.
     * A free string rather than a checked union: the registered domains ARE the source of truth,
     * so onboarding a partner is a row insert, not a code change and a deploy.
     */
    partner: string;
    zip: string;
    purchasePrice: number;
    location?: ISampleReportLocation;
    sampleId: string;
    /** Captured on the download landing page, so absent until this report is downloaded. */
    cell?: string;
    nmls?: string;
    /**
     * Set on first download. Not part of the limit — that is consumed at request time — but it
     * is the epic's KPI: a request only becomes a qualified lead once it has been downloaded.
     */
    downloadedAt?: Date;
    /** The request timestamp; set by mongoose timestamps. */
    createdAt?: Date;
    updatedAt?: Date;
}
